import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

/**
 * The search and filter row above a list.
 *
 * Pinned below the page header so it's reachable from anywhere in a long list.
 * On a phone it slides out of the way while scrolling down and returns on the
 * way up, because vertical space is scarce there. On a wider screen it simply
 * stays put — hiding it would solve a problem that doesn't exist and move
 * things under the pointer.
 */
export default function StickyFilters({ children, className }) {
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);

  useEffect(() => {
    // Below Tailwind's `lg`, where the sidebar collapses and space is tight.
    const small = window.matchMedia("(max-width: 1023px)");
    if (!small.matches) return undefined;

    let queued = false;
    const onScroll = () => {
      // Scroll fires far more often than the screen redraws. Doing the work
      // once per frame keeps a long list smooth.
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => {
        queued = false;
        const y = window.scrollY;
        // A small threshold, so a stray pixel or a bounce doesn't flap it.
        if (Math.abs(y - lastY.current) < 6) return;
        setHidden(y > lastY.current && y > 90);
        lastY.current = y;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      className={cn(
        "sticky top-[68px] z-10 py-3 bg-surface transition-transform duration-200",
        hidden && "-translate-y-full lg:translate-y-0",
        className
      )}
    >
      {children}
    </div>
  );
}
